import { asyncDebounce } from '~~/helpers/lodash/asyncDebounce';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import { ALLOWED_METHODS } from '~~/types/expondoMiddleware';
import { errorToString } from '~~/helpers/error/errorToString';
import type { CtAutocompleteHit } from '~~/types/productListing/CtAutocompleteHit';
import type { CtAutocompleteProductsParams } from '~~/types/productListing/CtSearchProductsParams';

const AUTOCOMPLETE_DEBOUNCE_MS = 300;

// Header autocomplete. Only the latest request's response is applied.
export function useCtAutocompleteSearch() {

  const hits = ref<CtAutocompleteHit[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const apiFetch = useApiFetch();
  const latestRequestId = ref(0);

  const fetchHits = async (query: string, limit: number) => {

    const requestId = ++latestRequestId.value;

    try {

      const endpoint = `${EXPONDO_MIDDLEWARE_ROUTES.CT.ROOT}${EXPONDO_MIDDLEWARE_ROUTES.CT.AUTOCOMPLETE_PRODUCTS}`;
      const fullPath = `${EXPONDO_MIDDLEWARE_ROUTES.ROOT}${endpoint}`;

      const response = await apiFetch<{ hits: CtAutocompleteHit[] }>(fullPath, {
        method: ALLOWED_METHODS.post,
        body: { query, limit } satisfies CtAutocompleteProductsParams,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
      });

      if (requestId !== latestRequestId.value) {

        return;

      }

      hits.value = response.hits;
      error.value = null;

    } catch (err: unknown) {

      if (requestId !== latestRequestId.value) {

        return;

      }

      error.value = errorToString(err);
      hits.value = [];

    } finally {

      if (requestId === latestRequestId.value) {

        loading.value = false;

      }

    }

  };

  const debouncedFetchHits = asyncDebounce(fetchHits, AUTOCOMPLETE_DEBOUNCE_MS);

  // Set loading before the debounce, otherwise "No results" flashes while it waits.
  const search = (query: string, limit: number) => {

    loading.value = true;

    return debouncedFetchHits(query, limit);

  };

  const clear = () => {

    latestRequestId.value += 1;
    hits.value = [];
    loading.value = false;
    error.value = null;

  };

  return {
    search,
    clear,
    hits: computed(() => hits.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
  };

}
